import React, {
  useCallback,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Radio } from 'antd';

import FormsTable from '../FormsTable';
import FormResponder from '../FormResponder';

import {
  getCustomers,
  getProjectIdMap,
} from '../../state/selectors';
import {
  getFilters,
  getParsedDrafts,
  handleFormClick,
} from '../formHelpers';
import { getFormsHomeDraftColumns } from '../FormColumns';
import {
  DRAFT_FILTER_ALL,
  DRAFT_FILTER_ASSIGNMENT,
  DRAFT_FILTER_SELF,
  DRAFT_SOURCE_ASSIGNMENT,
  FORMS_HOME_PREVIEW_LIMIT,
} from './formsHomeHelpers';
import OnTraccrRadioGroup from '../../common/inputs/OnTraccrRadioGroup';
import { formatProjectLabelFromCompanySettings } from '../../projects/projectHelpers';

export default function FormsHomeDraftsSection({ history }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    manual: manualDrafts = {},
    assigned: assignedDrafts = {},
  } = useSelector((state) => state.forms.drafts);
  const customers = useSelector(getCustomers);
  const projectIdMap = useSelector(getProjectIdMap);
  const templates = useSelector((state) => state.forms.templates);
  const { settings = {} } = useSelector((state) => state.settings.company);

  const [draftFilter, setDraftFilter] = useState(DRAFT_FILTER_ALL);
  const [loading, setLoading] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [assignedForm, setAssignedForm] = useState({});
  const [selectedIsEdit, setSelectedIsEdit] = useState(false);
  const [selectedIsResubmit, setSelectedIsResubmit] = useState(false);

  const {
    allDrafts,
    selfDrafts,
    assignmentDrafts,
  } = useMemo(() => getParsedDrafts({
    manualDrafts,
    assignedDrafts,
    customers,
    projectIdMap,
    templates,
  }), [manualDrafts, assignedDrafts, customers, projectIdMap, templates]);

  const filteredDrafts = useMemo(() => {
    if (draftFilter === DRAFT_FILTER_SELF) {
      return selfDrafts;
    }
    if (draftFilter === DRAFT_FILTER_ASSIGNMENT) {
      return assignmentDrafts;
    }
    return allDrafts;
  }, [allDrafts, selfDrafts, assignmentDrafts, draftFilter]);

  const previewData = useMemo(
    () => filteredDrafts.slice(0, FORMS_HOME_PREVIEW_LIMIT),
    [filteredDrafts],
  );

  const selfCount = selfDrafts.length;
  const assignmentCount = allDrafts.filter((d) => d.source === DRAFT_SOURCE_ASSIGNMENT).length;

  const assignedCustomerFilter = useMemo(
    () => getFilters({ data: previewData, idMap: customers, key: 'customers' }),
    [previewData, customers],
  );
  const assignedProjectFilter = useMemo(
    () => getFilters({
      data: previewData,
      idMap: projectIdMap,
      key: 'projects',
      formatter: ({ name, number }) => (
        { name: formatProjectLabelFromCompanySettings({ name, number, settings }) }
      ),
    }),
    [previewData, projectIdMap],
  );

  const onContinue = useCallback(async (record) => {
    await handleFormClick({
      record,
      dispatch,
      loading,
      setLoading,
      setAssignedForm,
      setShowDetail,
      setSelectedIsEdit,
      setSelectedIsResubmit,
    });
  }, [dispatch, loading]);

  const columns = useMemo(() => getFormsHomeDraftColumns({
    projectFilters: assignedProjectFilter,
    customerFilters: assignedCustomerFilter,
    projectIdMap,
    settings,
    t,
    onContinue,
  }), [
    assignedProjectFilter,
    assignedCustomerFilter,
    projectIdMap,
    settings,
    t,
    onContinue,
  ]);

  return (
    <section className="forms-home-section">
      <div className="forms-home-section-header">
        <div>
          <h2 className="forms-home-section-title">Drafts</h2>
          <p className="forms-home-section-subtitle">
            Pick up where you left off — all in-progress forms in one place
          </p>
        </div>
        <div className="forms-home-section-actions">
          <div className="forms-home-draft-filters">
            <OnTraccrRadioGroup
              value={draftFilter}
              size="medium"
              buttonStyle="outline"
              className="forms-home-tab-toggle"
              onChange={(value) => setDraftFilter(value)}
            >
              <Radio.Button value={DRAFT_FILTER_ALL}>
                All
                {' '}
                <span className="forms-home-tab-toggle-count">{allDrafts.length}</span>
              </Radio.Button>
              <Radio.Button value={DRAFT_FILTER_SELF}>
                Self-started
                {' '}
                <span className="forms-home-tab-toggle-count">{selfCount}</span>
              </Radio.Button>
              <Radio.Button value={DRAFT_FILTER_ASSIGNMENT}>
                From assignment
                {' '}
                <span className="forms-home-tab-toggle-count">{assignmentCount}</span>
              </Radio.Button>
            </OnTraccrRadioGroup>
          </div>
          <span
            className="forms-home-view-all"
            onClick={() => history.push('/forms/drafts')}
          >
            View all →
          </span>
        </div>
      </div>
      <FormsTable
        data={previewData}
        columns={columns}
        onClickRow={onContinue}
        loading={loading}
        isHomePreview
      />
      <FormResponder
        visible={showDetail}
        onClose={() => setShowDetail(false)}
        assignedForm={assignedForm}
        isEdit={selectedIsEdit}
        isResubmit={selectedIsResubmit}
      />
    </section>
  );
}

FormsHomeDraftsSection.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
};
